'use client';

import { useState, useEffect, useCallback } from 'react';
import type { Persona } from '../types/survey';
import { PERSONA_THEMES, type PersonaThemeConfig } from '../themes/personaTheme';
import type { RichPersonaProfile, MetaType } from '../types/richPersona';
import { getMetaTypeById } from '../data/metaTypes';

const STORAGE_KEY = 'survey_persona';
const RICH_PROFILE_KEY = 'rich_persona_profile';
const DEFAULT_PERSONA: Persona = 'university'; // 존댓말 기본값 (페르소나 미설정 시)
const VALID_PERSONAS: Persona[] = ['elementary', 'secondary', 'university', 'professional'];

/**
 * Hook to read the current persona from localStorage and provide theme config.
 *
 * Also sets `data-persona` attribute on <html> and applies CSS custom properties
 * so that global styles can adapt to the persona.
 *
 * Extended with rich persona profile (MetaType) support.
 */
export function usePersona(): {
  persona: Persona;
  theme: PersonaThemeConfig;
  setPersona: (p: Persona) => void;
  richProfile: RichPersonaProfile | null;
  metaType: MetaType | null;
  setRichProfile: (profile: RichPersonaProfile) => void;
} {
  const [persona, setPersonaState] = useState<Persona>(DEFAULT_PERSONA);
  const [richProfile, setRichProfileState] = useState<RichPersonaProfile | null>(null);

  // Read persona from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_PERSONAS.includes(stored as Persona)) {
      setPersonaState(stored as Persona);
    }

    // Read rich persona profile — discard old format (no metaTypeId)
    const richStored = localStorage.getItem(RICH_PROFILE_KEY);
    if (richStored) {
      try {
        const parsed = JSON.parse(richStored) as RichPersonaProfile;
        if (parsed.metaTypeId) {
          setRichProfileState(parsed);
        } else {
          // Old profile without metaTypeId → delete, user needs to retake discovery
          localStorage.removeItem(RICH_PROFILE_KEY);
        }
      } catch (_e: unknown) {
        localStorage.removeItem(RICH_PROFILE_KEY);
      }
    }
  }, []);

  // Apply data-persona attribute and CSS custom properties
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.setAttribute('data-persona', persona);

    const theme = PERSONA_THEMES[persona];
    root.style.setProperty('--persona-primary', theme.colors.primary);
    root.style.setProperty('--persona-primary-light', theme.colors.primaryLight);
    root.style.setProperty('--persona-primary-dark', theme.colors.primaryDark);
    root.style.setProperty('--persona-accent', theme.colors.accent);
    root.style.setProperty('--persona-surface', theme.colors.surface);
    root.style.setProperty('--persona-surface-border', theme.colors.surfaceBorder);
    root.style.setProperty('--persona-font-size', theme.typography.baseFontSize);
    root.style.setProperty('--persona-accent-light', theme.colors.accentLight);
  }, [persona]);

  const setPersona = useCallback((p: Persona) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, p);
    setPersonaState(p);
  }, []);

  const setRichProfile = useCallback((profile: RichPersonaProfile) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(RICH_PROFILE_KEY, JSON.stringify(profile));
    setRichProfileState(profile);
  }, []);

  const metaType = richProfile?.metaTypeId
    ? getMetaTypeById(richProfile.metaTypeId)
    : null;

  return {
    persona,
    theme: PERSONA_THEMES[persona],
    setPersona,
    richProfile,
    metaType,
    setRichProfile,
  };
}
